import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import RowActionsMenu from '../../dashboard/RowActionsMenu';
import { formatDateTime, formatPhone, formatRelative } from '../../../utils/formatters';
import ContactAvatar from '../ContactAvatar';
import ContactStatusChips from '../ContactStatusChips';

// Icon + value, the label only for screen readers and the tooltip.
function Field({ icon, label, value, sx }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, color: 'text.secondary', ...sx }}>
      {icon}
      <Typography variant="body2" color="text.secondary" noWrap title={`${label}: ${value}`} aria-label={`${label}: ${value}`}>
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * A contact as a card (list item of the card view). `to`: detail route, which
 * turns the name into a link — each app passes its own. `menuActions`: items
 * of the ⋮ in the header ([{ label, icon, onClick }], as in RowActionsMenu).
 * `actions`: buttons at the bottom, also the app's.
 *
 * Default (elevated) Card, never `outlined`: in the dark theme an outlined
 * card has no surface of its own and disappears into the background. Put
 * the cards on the page background, not inside another card: dark lightens
 * the Paper by elevation, and two cards a level apart look the same.
 */
export default function ContactCard({ contact, to, menuActions, actions }) {
  const { t, i18n } = useTranslation();
  const name = contact.display_name || t('notAvailable');
  const location = [contact.city, contact.state].filter(Boolean).join(', ');
  // Clock icon instead of a "Last message:" label: "há cerca de 2 meses" alone
  // already fills the subheader. The full date goes in the tooltip.
  const lastMessage = `${t('contacts.fields.lastMessage')}: ${formatDateTime(contact.last_message_at, i18n.language, { empty: t('notAvailable') })}`;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<ContactAvatar name={contact.display_name} size={48} />}
        title={to ? (
          <Link component={RouterLink} to={to} color="text.primary" underline="hover">
            {name}
          </Link>
        ) : name}
        subheader={(
          <Stack direction="row" spacing={0.5} alignItems="center" component="span" title={lastMessage} aria-label={lastMessage}>
            <ScheduleOutlinedIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" noWrap component="span">
              {formatRelative(contact.last_message_at, i18n.language, { empty: t('notAvailable') })}
            </Typography>
          </Stack>
        )}
        action={menuActions?.length > 0 && <RowActionsMenu actions={menuActions} ariaLabel={t('contacts.actions.menu')} />}
        slotProps={{
          title: { variant: 'subtitle1', noWrap: true },
          subheader: { component: 'div' },
        }}
        // Lets the title/subheader shrink and ellipsize next to the avatar and ⋮.
        sx={{ '& .MuiCardHeader-content': { minWidth: 0 } }}
      />
      <Divider sx={{ mx: 2 }} />
      <CardContent sx={{ flex: 1 }}>
        <Stack spacing={1}>
          <ContactStatusChips contact={contact} />
          {/* One per row: a full phone (+55 21 90000-0000) doesn't fit half a card. */}
          <Field
            icon={<PhoneOutlinedIcon fontSize="small" />}
            label={t('contacts.fields.phone')}
            value={contact.phone_number ? formatPhone(contact.phone_number) : t('notAvailable')}
          />
          <Field
            icon={<LocationOnOutlinedIcon fontSize="small" />}
            label={t('contacts.fields.location')}
            value={location || t('notAvailable')}
          />
          {/* E-mail is rare (the AI only sometimes collects it): no empty row. */}
          {contact.email && (
            <Field icon={<EmailOutlinedIcon fontSize="small" />} label={t('contacts.fields.email')} value={contact.email} />
          )}
        </Stack>
      </CardContent>
      {actions && <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>{actions}</CardActions>}
    </Card>
  );
}
